import { useEffect, useState } from 'react';
import { mapAccentForRole } from '../data/portfolioNodes';

function descriptionParagraphs(text) {
  if (!text || typeof text !== 'string') return [];
  return text.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
}

const memoryCache = new Map();

function loadMemories(fsId) {
  if (memoryCache.has(fsId)) return Promise.resolve(memoryCache.get(fsId));
  return fetch(`/ancestors/memories/people/${fsId}.json`)
    .then((res) => {
      if (!res.ok) return [];
      return res.json().then((list) => {
        const items = Array.isArray(list) ? list : [];
        memoryCache.set(fsId, items);
        return items;
      });
    })
    .catch(() => []);
}

function MemoryGallery({ fsId, onImageOpen }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!fsId) {
      setItems([]);
      return undefined;
    }
    let cancel = false;
    loadMemories(fsId).then((list) => {
      if (!cancel) setItems(list);
    });
    return () => {
      cancel = true;
    };
  }, [fsId]);

  if (!items?.length) return null;
  const images = items.filter((item) => item.kind === 'image' && item.thumb && item.full);
  const docs = items.filter((item) => item.kind === 'pdf' && item.href);
  const stories = items.filter((item) => item.kind === 'story' && item.text);

  return (
    <div className="detail-memories">
      <p className="detail-section-label">Memories</p>
      {images.length ? (
        <div className="detail-memory-grid">
          {images.map((item) => (
            <button
              type="button"
              key={item.id}
              className="detail-memory-thumb"
              onClick={() => onImageOpen({
                src: item.full,
                title: item.title && item.title !== 'Photo' ? item.title : '',
                caption: item.description || '',
              })}
            >
              <img src={item.thumb} alt={item.title || ''} />
            </button>
          ))}
        </div>
      ) : null}
      {docs.length ? (
        <ul className="detail-memory-docs">
          {docs.map((item) => (
            <li key={item.id}>
              <a className="detail-link" href={item.href} target="_blank" rel="noopener noreferrer">
                {item.title || 'Document'}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {stories.map((item) => (
        <div key={item.id} className="detail-story">
          <p className="detail-story-title">{item.title || 'Story'}</p>
          {descriptionParagraphs(item.text).map((para, index) => (
            <p key={index}>{para}</p>
          ))}
        </div>
      ))}
    </div>
  );
}

function loadDetails(fsId) {
  return fetch(`/ancestors/details/${fsId}.json`)
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);
}

function mergeEvents(events, facts) {
  const base = events || [];
  if (!facts?.length) return base;
  const seen = new Set(base.map((event) => [event.kind, event.date, event.place, event.note].join('|').toLowerCase()));
  return base.concat(facts.filter((fact) => {
    const key = [fact.kind, fact.date, fact.place, fact.note].join('|').toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }));
}

export default function DetailPanel({ node, onClose, onImageOpen }) {
  const fsId = node?.fsId || '';
  const [extra, setExtra] = useState(null);

  useEffect(() => {
    if (!fsId) {
      setExtra(null);
      return undefined;
    }
    let cancel = false;
    loadDetails(fsId).then((data) => {
      if (!cancel) setExtra(data);
    });
    return () => {
      cancel = true;
    };
  }, [fsId]);

  if (!node) return null;

  const description = node.description || extra?.sketch || '';
  const bodyParas = descriptionParagraphs(description);
  const events = mergeEvents(node.events, extra?.facts);
  const sources = node.sources?.length ? node.sources : (extra?.sources || []);
  const gallery = node.images?.length ? (
    <div className="detail-images">
      {node.images.map((src) => (
        <button
          type="button"
          key={src}
          className="detail-thumb"
          onClick={() => onImageOpen(src)}
        >
          <img src={src} alt="" />
        </button>
      ))}
    </div>
  ) : null;
  const statusColor = node.mapRole ? mapAccentForRole(node.mapRole) : node.accent;

  return (
    <div className="detail-backdrop" onClick={onClose} role="presentation">
      <aside
        className="detail-panel anim-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="detail-title"
      >
        <button type="button" className="detail-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4.25 4.25l7.5 7.5M11.75 4.25l-7.5 7.5" />
          </svg>
        </button>
        <div className="detail-panel-glow" aria-hidden />
        <h2 id="detail-title" className="detail-title">
          {node.title}
        </h2>
        <p className="detail-subtitle">{node.subtitle}</p>
        {node.portrait ? (
          <button type="button" className="detail-portrait" onClick={() => onImageOpen(node.portrait)}>
            <img src={node.portrait} alt="" />
          </button>
        ) : null}
        {node.alsoKnown?.length ? (
          <p className="detail-aka">Also known as {node.alsoKnown.join(', ')}</p>
        ) : null}
        {node.status ? (
          <p
            className="detail-meta detail-status"
            style={statusColor ? { color: statusColor } : undefined}
          >
            {node.status}
          </p>
        ) : null}
        {node.date ? <p className="detail-meta detail-date">{node.date}</p> : null}
        {events?.length ? (
          <ol className="detail-events">
            {events.map((event, index) => (
              <li key={`${event.kind}-${event.date || ''}-${index}`} className="detail-event" style={{ '--event-color': node.accent || '#7dcf9a' }}>
                <p className="detail-event-kind">{event.kind}</p>
                {event.date ? <p className="detail-event-date">{event.date}</p> : null}
                {event.place ? <p className="detail-event-place">{event.place}</p> : null}
                {event.note ? <p className="detail-event-note">{event.note}</p> : null}
              </li>
            ))}
          </ol>
        ) : null}
        {node.id === 'kai' ? gallery : null}
        {bodyParas.length ? (
          <div className="detail-body-stack">
            {bodyParas.map((para, i) => (
              <p key={i} className="detail-body">
                {para}
              </p>
            ))}
          </div>
        ) : null}
        <MemoryGallery fsId={node.fsId} onImageOpen={onImageOpen} />
        {sources?.length ? (
          <div className="detail-sources">
            <p className="detail-section-label">Sources</p>
            <ul>
              {sources.map((source, index) => (
                <li key={`${source.href || source.title}-${index}`}>
                  {source.href ? (
                    <a className="detail-link" href={source.href} target="_blank" rel="noopener noreferrer">
                      {source.title}
                    </a>
                  ) : (
                    <span className="detail-source-title">{source.title}</span>
                  )}
                  {source.citation ? <p className="detail-source-cite">{source.citation}</p> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {node.links?.length ? (
          <ul className="detail-links">
            {node.links.map((link, i) => {
              if (!link) return null;
              if (link.separator) {
                return <li key={link.key ?? `sep-${i}`} className="detail-links-separator" aria-hidden />;
              }
              const { href, label } = link;
              if (!href || !label) return null;
              return (
                <li key={href}>
                  <a className="detail-link" href={href} target="_blank" rel="noopener noreferrer">
                    {label}
                  </a>
                </li>
              );
            })}
          </ul>
        ) : null}
        {node.id === 'kai' ? null : gallery}
        {node.articles?.length ? (
          <div className="detail-articles">
            <p className="detail-section-label">Articles</p>
            <ul className="detail-links">
              {node.articles.map((article) => {
                if (!article?.href || !article?.label) return null;
                return (
                  <li key={article.href}>
                    <a className="detail-link" href={article.href} target="_blank" rel="noopener noreferrer">
                      {article.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
